export interface Transfer {
  from: string;
  to: string;
  amount: number; // paise
}

// Greedy min-cash-flow settlement: repeatedly match the largest debtor with the
// largest creditor. Produces at most (participants - 1) transfers and settles
// every balance exactly. Deterministic: ties break on pubkey.
export function simplifyDebts(balances: Map<string, number>): Transfer[] {
  const creditors: Array<{ pub: string; amt: number }> = [];
  const debtors: Array<{ pub: string; amt: number }> = [];
  let sum = 0;
  for (const [pub, amt] of balances) {
    if (!Number.isSafeInteger(amt)) throw new Error("balances must be integers");
    sum += amt;
    if (amt > 0) creditors.push({ pub, amt });
    else if (amt < 0) debtors.push({ pub, amt: -amt });
  }
  if (sum !== 0) throw new Error(`cannot settle: balances sum to ${sum}, not 0`);

  const byAmtDescPub = (a: { pub: string; amt: number }, b: { pub: string; amt: number }) =>
    b.amt === a.amt ? (a.pub < b.pub ? -1 : 1) : b.amt - a.amt;
  creditors.sort(byAmtDescPub);
  debtors.sort(byAmtDescPub);

  const transfers: Transfer[] = [];
  let ci = 0;
  let di = 0;
  while (ci < creditors.length && di < debtors.length) {
    const c = creditors[ci] as { pub: string; amt: number };
    const d = debtors[di] as { pub: string; amt: number };
    const amount = Math.min(c.amt, d.amt);
    transfers.push({ from: d.pub, to: c.pub, amount });
    c.amt -= amount;
    d.amt -= amount;
    if (c.amt === 0) ci++;
    if (d.amt === 0) di++;
  }
  if (ci !== creditors.length || di !== debtors.length) {
    throw new Error("simplifyDebts: failed to settle all balances");
  }
  return transfers;
}
